import {
    Button,
} from "@mrflark/ui";
import {
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import {
    Loader2,
    Settings,
} from "lucide-preact";
import {
    type ReactElement,
    useEffect,
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";
import {
    splClient,
} from "../../api/client.ts";

type SettingFieldConfig = {
    key: string;
    label: string;
    placeholder: string;
};

const SETTING_FIELDS: SettingFieldConfig[] = [
    {
        key: "ai_description_instructions",
        label: "Description Guidance",
        placeholder:
            "Describe the image in 1-2 sentences. Focus on what makes it funny or notable.",
    },
    {
        key: "ai_tag_instructions",
        label: "Tag Selection Guidance",
        placeholder:
            "Select the most relevant tags from the provided list. Only use tags that clearly apply.",
    },
    {
        key: "ai_object_instructions",
        label: "Object Detection Guidance",
        placeholder:
            "List any notable objects, people, characters, or text visible in the image.",
    },
];

const SettingField = ({
    config,
    fetchedValue,
}: {
    config: SettingFieldConfig;
    fetchedValue: string;
}): ReactElement => {
    const queryClient = useQueryClient();
    const [localValue, setLocalValue] = useState(fetchedValue);

    // sync local state when fetched data changes (e.g. after invalidation)
    useEffect(() => {
        setLocalValue(fetchedValue);
    }, [fetchedValue]);

    const isDirty = localValue !== fetchedValue;

    const saveMutation = useMutation({
        mutationFn: () => splClient.setSetting(config.key, localValue),
        onSuccess: () => {
            toast.success(`${config.label} saved`);
            queryClient.invalidateQueries({
                queryKey: ["adminSettings"],
            });
        },
        onError: (error: Error) => toast.error(`Save failed: ${error.message}`),
    });

    return (
        <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
                {config.label}
            </label>
            <textarea
                value={localValue}
                onChange={(event) =>
                    setLocalValue((event.target as HTMLInputElement).value)}
                placeholder={config.placeholder}
                rows={3}
                className="w-full px-3 py-2 bg-bg-tertiary border border-border rounded text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-accent resize-y"
            />
            <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-text-muted">
                    {localValue ? "" : "Using default when empty"}
                </span>
                <Button
                    onClick={() => saveMutation.mutate()}
                    disabled={!isDirty || saveMutation.isPending}
                    size="sm"
                    variant="primary"
                >
                    {saveMutation.isPending
                        ? <Loader2 size={14} className="animate-spin" />
                        : (
                            "Save"
                        )}
                </Button>
            </div>
        </div>
    );
};

export const AiPromptSettings = (): ReactElement => {
    const {
        data,
        isLoading,
    } = useQuery({
        queryKey: ["adminSettings"],
        queryFn: () => splClient.getSettings(),
    });

    const settings = data?.settings ?? {};

    return (
        <div className="bg-bg-secondary rounded-lg border border-border">
            <div className="p-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <Settings size={18} className="text-accent" />
                    <h2 className="text-lg font-semibold">
                        AI Prompt Settings
                    </h2>
                </div>
                <p className="text-sm text-text-muted mt-1">
                    Customize the instructions sent to the AI when analyzing
                    images. Leave empty to use defaults.
                </p>
            </div>
            <div className="p-4 space-y-6">
                {isLoading
                    ? (
                        <div className="p-8 text-center text-text-secondary">
                            <Loader2
                                size={20}
                                className="mx-auto mb-2 animate-spin text-accent"
                            />
                            <p className="text-sm">Loading settings...</p>
                        </div>
                    )
                    : (
                        SETTING_FIELDS.map((config) => (
                            <SettingField
                                key={config.key}
                                config={config}
                                fetchedValue={settings[config.key] ?? ""}
                            />
                        ))
                    )}
            </div>
        </div>
    );
};
